import React, { useState } from "react";

const SearchBar = ({
  searchQuery,
  handleSearch,
  handleSearchClick,
  selectedModel,
  handleModelChange,
  setLimit,
  searchType,
  setSearchType,
  setSearchQuery, // Add this line

}) => {
  const [customLimit, setCustomLimit] = useState(5);
  const [questionId, setQuestionId] = useState("");
  const [id, setId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCustomLimitChange = (e) => {
    const value = e.target.value;
    if (value < 1) {
      setErrorMessage("Limit must be a positive number.");
    } else {
      setErrorMessage("");
    }
    setCustomLimit(value);
  };

  const handleSearchButtonClick = async () => {
    let search = "";

    // Determine the search value based on the selected search type
    if (searchType === "questionId") {
      if (!questionId) {
        setErrorMessage("Question ID cannot be empty.");
        return;
      }
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(questionId);
      if (!isValidObjectId) {
        setErrorMessage("Invalid Question ID format.");
        return;
      }
      search = questionId;
    } else if (searchType === "id") {
      if (!id) {
        setErrorMessage("ID cannot be empty.");
        return;
      }
      const isValidNumber = !isNaN(id) && Number.isInteger(+id);
      if (!isValidNumber) {
        setErrorMessage("ID must be a valid number.");
        return;
      }
      search = id;
    } else {
      search = searchQuery; // For "name" (title)
    }

    // Reset error and set loading state
    setErrorMessage("");
    setLoading(true);

    const searchParams = {
      model: selectedModel,
      searchType,
      search: search || "",
      limit: Number(customLimit) || 5,
      page: 1,
    };

    console.log("Search Parameters:", searchParams);

    try {
      await handleSearchClick(searchParams);
    } catch (error) {
      setErrorMessage("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }

    setLimit(Number(customLimit) || 5);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col space-y-4">
      <div className="flex justify-between items-center space-x-2">
        {searchType === "name" && (
          <input
            type="text"
            placeholder="Search by title, question, or audio script..."
            value={searchQuery}
            onChange={handleSearch}
            className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Search by title, question, or audio script"
          />
        )}
        {searchType === "questionId" && (
          <input
            type="text"
            placeholder="Search by Question ID"
            value={questionId}
            onChange={(e) => {
              const value = e.target.value.toLowerCase(); // Convert to lowercase
              setQuestionId(value); // Update questionId for question ID search
              setSearchQuery(value); // Update searchQuery with questionId value
              handleSearch(e); // Call handleSearch to update search state
            }}
            className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Search by Question ID"
          />
        )}
        {searchType === "id" && (
          <input
            type="text"
            placeholder="Search by ID"
            value={id}
            onChange={(e) => {
              const value = e.target.value; // Get the current value
              setId(value); // Handle id state
              setSearchQuery(value); // Update searchQuery with id value
              handleSearch(e); // Call handleSearch to update search state
            }}
            className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Search by ID"
          />
        )}

        <select
          value={selectedModel}
          onChange={handleModelChange}
          className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Model</option>
          <optgroup label="Speaking Models">
            <option value="readaloud">Read Aloud</option>
            <option value="repeatsentence">Repeat Sentence</option>
            <option value="describeimage">Describe Image</option>
            <option value="retelllecture">Retell Lecture</option>
            <option value="answershortquestion">Answer Short Question</option>
          </optgroup>
          <optgroup label="Writing Models">
            <option value="summarizetext">Summarize Text</option>
            <option value="writeessay">Write Essay</option>
          </optgroup>
          <optgroup label="Reading Models">
            <option value="singleanswer">Single Answer</option>
            <option value="doubleanswer">Double Answer</option>
            <option value="reorderparagraph">Reorder Paragraph</option>
            <option value="fillintheblank">Fill in the Blank</option>
          </optgroup>
          <optgroup label="Listening Models">
            <option value="summarizespokentext">Summarize Spoken Text</option>
            <option value="writefromdictation">Write from Dictation</option>
            <option value="highlightsummary">
              Highlight Correct Summary
            </option>
            <option value="missingword">Select Missing Word</option>
            <option value="incorrectword">
              Highlight Incorrect Word
            </option>
          </optgroup>
        </select>

        <button
          onClick={handleSearchButtonClick}
          className={`bg-blue-600 text-white rounded-lg p-2 hover:bg-blue-700 transition ease-in-out ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={loading}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-sm">Search By:</span>
        <select
          value={searchType}
          onChange={(e) => {
            const newSearchType = e.target.value;
            setSearchType(newSearchType);
            // Clear relevant state based on selected search type
            if (newSearchType === "name") {
              setQuestionId("");
              setId("");
            } else if (newSearchType === "questionId") {
              setId(""); // Clear ID when switching to Question ID
            } else if (newSearchType === "id") {
              setQuestionId(""); // Clear Question ID when switching to ID
            }
          }}
          className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="name">Title/Question/Audio Script</option>
          <option value="questionId">Question ID</option>
          <option value="id">ID</option>
        </select>
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-sm">Items per page:</span>
        <input
          type="number"
          value={customLimit}
          onChange={handleCustomLimitChange}
          className="border border-gray-300 rounded-lg p-2 w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
          min={1}
          placeholder="Custom"
          aria-label="Custom limit for items per page"
        />
      </div>

      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
    </div>
  );
};

export default SearchBar;
